import { memo, useCallback, useMemo, useState } from "react";
import ReactDOM from "react-dom";

import { Button } from "ui/components/Button/Button";
import InvoiceIncomingAllocationBadge from "../InvoiceIncomingAllocationBadge/InvoiceIncomingAllocationBadge";
import InvoiceIncomingHeader from "./components/InvoiceIncomingHeader/InvoiceIncomingHeader";
import InvoiceIncomingItems from "./components/InvoiceIncomingItems/InvoiceIncomingItems";
import InvoiceIncomingTotal from "./components/InvoiceIncomingTotal/InvoiceIncomingTotal";
import InvoiceIncomingNotes from "./components/InvoiceIncomingNotes/InvoiceIncomingNotes";
import { INVOICE_INCOMING_ALLOCATABLE_STATUSES } from "services/api/finance/incoming-invoices/consts";
import { InvoiceIncomingInfoProps } from "./types";

const InvoiceIncomingInfo: React.FC<InvoiceIncomingInfoProps> = (props) => {
  const { invoice, allocations, formValues, setFormValues } = props;

  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const items = useMemo(() => invoice.items?.filter((i) => !i.deleted) ?? [], [invoice]);

  // Prije odobrenja raspodjela nije ni na redu, pa se stavke tada ne ističu.
  const isAllocatable = INVOICE_INCOMING_ALLOCATABLE_STATUSES.includes(invoice.status?.id ?? "");

  const handleExpandToggle = useCallback(() => {
    setIsExpanded(previous => !previous);
  }, []);

  const renderItemsSection = () => (
    <div>
      {/* Stanje raspodjele stoji uz naslov popisa — ondje gdje se i vidi što je raspoređeno. */}
      <div className="px-2 py-2 flex items-center justify-between gap-3 text-sm font-medium border-b-2 border-sky-600 text-sky-600">
        <span>Stavke računa</span>
        <InvoiceIncomingAllocationBadge allocations={allocations} isAllocatable={isAllocatable} />
      </div>

      <div className="border-x border-b border-slate-300 rounded-b-md">
        {/* Jedna stavka se vidi i bez razvlačenja, pa se gumb nudi tek kad ih ima više. */}
        {items.length > 1 && (
          <div className="flex justify-end px-3 pt-3">
            <Button
              variant="outlined"
              size="small"
              startIcon="/media/icons/scaling.svg"
              onClick={handleExpandToggle}
            />
          </div>
        )}

        <InvoiceIncomingItems items={items} allocations={allocations} isAllocatable={isAllocatable} />
        <InvoiceIncomingTotal invoice={invoice} />
      </div>
    </div>
  );

  return (
    <div>
      {/* Header: dobavljač + datumi */}
      <InvoiceIncomingHeader
        formValues={formValues}
        setFormValues={setFormValues}
      />

      {items.length > 0 && (
        isExpanded
          // Razvučeni popis ide u portal: `fixed` unutar modala bi ga odrezala kartica, koja se
          // skrola. Zauzima desnu polovicu, da prikaz s lijeve strane ostane vidljiv.
          ? ReactDOM.createPortal(
            <div className="fixed top-0 bottom-0 left-0 lg:left-1/2 right-0 z-[60] bg-white border-l border-slate-200 overflow-y-auto p-4">
              {renderItemsSection()}
            </div>,
            document.body
          )
          : renderItemsSection()
      )}

      {/* Napomena */}
      <InvoiceIncomingNotes note={invoice.note} />
    </div>
  );
};

export default memo(InvoiceIncomingInfo);
